const roundMoney = (value = 0) => Math.round((Number(value) + Number.EPSILON) * 100) / 100;

export const formatTreasuryMoney = (value = 0) => `${roundMoney(value).toFixed(2)} €`;

export const getTreasurySourceTypeLabel = (type) => {
    const labels = {
        banca: 'Conto corrente',
        carta: 'Carta aziendale',
        cassa: 'Cassa contanti',
        posta: 'Posta',
    };

    return labels[type] || type;
};

export const getPayableStatusLabel = (status) => {
    const labels = {
        da_pagare: 'Da pagare',
        parzialmente_pagata: 'Parzialmente pagata',
        pagata: 'Pagata',
    };

    return labels[status] || status;
};

export const getPayableStatusColor = (status) => {
    const colors = {
        da_pagare: 'warning',
        parzialmente_pagata: 'processing',
        pagata: 'success',
    };

    return colors[status] || 'default';
};

const buildDocumentTotalsMap = (passiveWorkspace = {}, documents = []) => {
    const map = {};

    Object.values(passiveWorkspace.invoices || {}).forEach((invoice) => {
        map[invoice.id] = roundMoney(invoice.computed?.totals?.xmlGross || invoice.computed?.totals?.gross || 0);
    });

    documents.forEach((document) => {
        if (typeof map[document.id] === 'undefined') {
            map[document.id] = roundMoney(Math.abs(document.amount || 0));
        }
    });

    return map;
};

const enrichPayables = (workspace, passiveWorkspace, documents) => {
    const documentTotalsMap = buildDocumentTotalsMap(passiveWorkspace, documents);
    const creditsById = (workspace.supplierCredits || []).reduce((accumulator, credit) => {
        accumulator[credit.id] = credit;
        return accumulator;
    }, {});

    return (workspace.payables || []).map((payable) => {
        const documentTotal = payable.linkedDocumentId ? documentTotalsMap[payable.linkedDocumentId] : null;
        const total = roundMoney(documentTotal ?? payable.total ?? 0);
        const paidAmount = roundMoney((payable.paymentHistory || []).reduce((sum, payment) => sum + Number(payment.amount || 0), 0));
        const compensatedAmount = roundMoney((payable.appliedCredits || []).reduce((sum, credit) => sum + Number(credit.amount || 0), 0));
        const residual = roundMoney(total - paidAmount - compensatedAmount);
        const status = residual <= 0 ? 'pagata' : (paidAmount > 0 || compensatedAmount > 0 ? 'parzialmente_pagata' : 'da_pagare');
        const availableCredits = (payable.creditIds || [])
            .map((creditId) => creditsById[creditId])
            .filter(Boolean)
            .map((credit) => ({
                ...credit,
                availableAmount: roundMoney((credit.total || 0) - (credit.usedAmount || 0)),
            }));

        return {
            ...payable,
            total,
            paidAmount,
            compensatedAmount,
            residual,
            status,
            availableCredits,
        };
    });
};

const enrichSupplierCredits = (workspace) => {
    return (workspace.supplierCredits || []).map((credit) => ({
        ...credit,
        usedAmount: roundMoney(credit.usedAmount || 0),
        availableAmount: roundMoney((credit.total || 0) - (credit.usedAmount || 0)),
    }));
};

const enrichSources = (workspace) => {
    return (workspace.sources || []).map((source) => {
        const movementDelta = roundMoney((workspace.bankMovements || [])
            .filter((movement) => movement.sourceId === source.id)
            .reduce((sum, movement) => sum + Number(movement.amount || 0), 0));

        const expectedBalance = roundMoney(Number(source.openingBalance || 0) + movementDelta);
        const actualBalance = roundMoney(source.actualBalance || 0);
        const variance = roundMoney(actualBalance - expectedBalance);

        return {
            ...source,
            expectedBalance,
            actualBalance,
            variance,
        };
    });
};

const buildSuggestions = (workspace, payables = []) => {
    const suggestions = [];
    const payablesById = payables.reduce((accumulator, payable) => {
        accumulator[payable.id] = payable;
        return accumulator;
    }, {});

    (workspace.bankMovements || []).forEach((movement) => {
        if (movement.reconciledPayableId) {
            return;
        }

        const absoluteAmount = Math.abs(Number(movement.amount || 0));
        const normalizedCounterparty = `${movement.counterpartyName || ''} ${movement.description || ''}`.toLowerCase();

        payables.forEach((payable) => {
            if (payable.residual <= 0) {
                return;
            }

            const sameCompany = payable.companyDocumentId === movement.companyDocumentId;
            const supplierMatch = normalizedCounterparty.includes((payable.supplierName || '').toLowerCase());
            const amountMatch = Math.abs(roundMoney(payable.residual) - absoluteAmount) <= 2;

            if (!sameCompany || (!supplierMatch && !amountMatch)) {
                return;
            }

            const confidence = supplierMatch && amountMatch ? 0.96 : supplierMatch ? 0.81 : 0.72;
            suggestions.push({
                id: `suggestion-${movement.id}-${payable.id}`,
                movementId: movement.id,
                payableId: payable.id,
                supplierName: payable.supplierName,
                amount: absoluteAmount,
                confidence,
                reason: amountMatch && supplierMatch
                    ? 'Importo e controparte coerenti'
                    : supplierMatch
                        ? 'Controparte coerente'
                        : 'Importo vicino al residuo aperto',
            });
        });
    });

    return suggestions
        .sort((left, right) => right.confidence - left.confidence)
        .map((suggestion) => ({
            ...suggestion,
            payable: payablesById[suggestion.payableId] || null,
        }));
};

const buildSupplierLedgers = (payables = [], supplierCredits = []) => {
    const grouped = {};

    payables.forEach((payable) => {
        if (!grouped[payable.supplierName]) {
            grouped[payable.supplierName] = {
                supplierName: payable.supplierName,
                years: new Set(),
                openingBalance: 0,
                movements: [],
            };
        }

        const bucket = grouped[payable.supplierName];
        bucket.years.add(String(payable.year || new Date(payable.dueDate).getFullYear()));
        bucket.movements.push({
            id: `ledger-${payable.id}`,
            date: payable.issueDate || payable.dueDate,
            type: 'fattura',
            description: payable.label,
            amount: roundMoney(payable.total),
            linkedDocumentId: payable.linkedDocumentId || null,
        });

        (payable.paymentHistory || []).forEach((payment) => {
            bucket.movements.push({
                id: `ledger-payment-${payment.id}`,
                date: payment.date,
                type: 'pagamento',
                description: payment.sourceLabel,
                amount: roundMoney(-payment.amount),
            });
        });

        (payable.appliedCredits || []).forEach((credit) => {
            bucket.movements.push({
                id: `ledger-credit-${credit.id}`,
                date: credit.date,
                type: 'nota_credito',
                description: credit.label,
                amount: roundMoney(-credit.amount),
            });
        });
    });

    supplierCredits.forEach((credit) => {
        if (!grouped[credit.supplierName]) {
            grouped[credit.supplierName] = {
                supplierName: credit.supplierName,
                years: new Set(),
                openingBalance: 0,
                movements: [],
            };
        }

        grouped[credit.supplierName].years.add(String(credit.year || new Date(credit.issuedAt).getFullYear()));
    });

    return Object.values(grouped).map((ledger) => {
        const orderedMovements = [...ledger.movements].sort((left, right) => left.date.localeCompare(right.date));
        let progressive = ledger.openingBalance;

        return {
            supplierName: ledger.supplierName,
            years: Array.from(ledger.years).sort(),
            openingBalance: ledger.openingBalance,
            movements: orderedMovements.map((movement) => {
                progressive = roundMoney(progressive + movement.amount);
                return {
                    ...movement,
                    progressiveBalance: progressive,
                };
            }),
            closingBalance: progressive,
        };
    });
};

export const recalculateTreasuryWorkspace = ({ treasuryWorkspace = {}, passiveWorkspace = {}, documents = [] }) => {
    const nextWorkspace = {
        ...treasuryWorkspace,
        sources: [...(treasuryWorkspace.sources || [])],
        payables: [...(treasuryWorkspace.payables || [])],
        supplierCredits: [...(treasuryWorkspace.supplierCredits || [])],
        bankMovements: [...(treasuryWorkspace.bankMovements || [])],
        imports: [...(treasuryWorkspace.imports || [])],
    };

    nextWorkspace.supplierCredits = enrichSupplierCredits(nextWorkspace);
    nextWorkspace.payables = enrichPayables(nextWorkspace, passiveWorkspace, documents);
    nextWorkspace.sources = enrichSources(nextWorkspace);
    nextWorkspace.suggestions = buildSuggestions(nextWorkspace, nextWorkspace.payables);
    nextWorkspace.supplierLedgers = buildSupplierLedgers(nextWorkspace.payables, nextWorkspace.supplierCredits);
    nextWorkspace.summary = {
        totalPayables: roundMoney(nextWorkspace.payables.reduce((sum, payable) => sum + payable.residual, 0)),
        totalCredits: roundMoney(nextWorkspace.supplierCredits.reduce((sum, credit) => sum + credit.availableAmount, 0)),
        unreconciledMovements: nextWorkspace.bankMovements.filter((movement) => !movement.reconciledPayableId).length,
        variance: roundMoney(nextWorkspace.sources.reduce((sum, source) => sum + source.variance, 0)),
    };

    return nextWorkspace;
};
